import React, { ReactElement } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import {
  gray110,
  gray80,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd } from '../../../constants/location';
import { ReactComponent as PatternCompleteIcon } from '../../assets/pattern_complete.svg';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';

type Props = {
  open: boolean;
  onClose: () => void;
  patternId: string;
  uploadedFiles?: File[];
  onOcrStart?: () => void;
};

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      borderRadius: '8px',
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px 40px 32px 40px',
      width: '560px',
      minHeight: '480px',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      display: 'flex',
      flexDirection: 'column',
      '@media screen and (max-width: 600px)': {
        padding: '32px 16px',
        minHeight: '400px',
      },
    },
  },
  closeButton: {
    position: 'absolute',
    right: '24px',
    top: '16px',
    padding: 0,
  },
  icon: {
    height: '28px',
    width: '28px',
    color: '#808080',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    flex: 1,
  },
  imageWrapper: {
    marginBottom: '32px',
    marginTop: '16px',
    width: '230px',
    height: '125px',
  },
  title: {
    color: gray110,
    fontSize: '22px',
    fontWeight: 500,
    letterSpacing: '0.1px',
    marginBottom: '24px',
  },
  fileSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'stretch',
    width: '100%',
    maxWidth: '400px',
  },
  description: {
    color: gray110,
    fontSize: '14px',
    lineHeight: 1.7,
    fontWeight: 400,
    marginBottom: '8px',
    textAlign: 'center' as const,
  },
  fileListBox: {
    backgroundColor: '#F5F5F5',
    borderRadius: '4px',
    padding: '10px 14px',
    width: '100%',
  },
  fileListItem: {
    color: gray80,
    fontSize: '14px',
    lineHeight: 1.8,
    wordBreak: 'break-all' as const,
    textAlign: 'left' as const,
  },
  creditNote: {
    color: gray110,
    fontSize: '14px',
    fontWeight: 400,
    marginTop: '12px',
    marginBottom: '24px',
    lineHeight: 1.7,
    textAlign: 'center' as const,
  },
  buttonWrapper: {
    display: 'flex',
    gap: '12px',
    width: '100%',
  },
  skipButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    flex: 1,
    borderRadius: '4px',
  },
  skipButtonText: {
    ...boldMd,
    color: gray80,
  },
  ocrButton: {
    ...primaryButtonBackgroundColor,
    height: '44px',
    flex: 1,
    borderRadius: '4px',
  },
  ocrButtonText: {
    ...boldMd,
    color: 'white',
  },
  singleDescription: {
    color: gray110,
    fontSize: '14px',
    fontWeight: 400,
    marginBottom: '32px',
    lineHeight: 1.7,
  },
  singleButton: {
    ...primaryButtonBackgroundColor,
    height: '44px',
    width: '100%',
    maxWidth: '280px',
    borderRadius: '4px',
  },
  singleButtonText: {
    ...boldMd,
    fontSize: '16px',
    color: 'white',
  },
};

const PatternCompleteModal = ({
  open,
  onClose,
  patternId,
  uploadedFiles,
  onOcrStart,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();
  const navigate = useNavigate();

  const hasFiles = uploadedFiles && uploadedFiles.length > 0;

  const handleNavigateToOcrList = (): void => {
    onClose();
    navigate(`/ai-ocr/pattern/${patternId}`);
  };

  const handleOcrStart = (): void => {
    onOcrStart?.();
    handleNavigateToOcrList();
  };

  return (
    <Dialog open={open} sx={[styles.dialog, proxyDialogSx]} onClose={onClose}>
      <IconButton onClick={onClose} sx={styles.closeButton}>
        <Close sx={styles.icon} />
      </IconButton>
      <Box sx={styles.content}>
        <Box sx={styles.imageWrapper}>
          <PatternCompleteIcon />
        </Box>
        <Typography sx={styles.title}>読取パターンが完成しました</Typography>

        {hasFiles ? (
          <Box sx={styles.fileSection}>
            <Typography sx={styles.description}>
              以下のファイルの文字読取を実行しますか？
            </Typography>
            <Box sx={styles.fileListBox}>
              {uploadedFiles.map((file, index) => (
                <Typography key={index} sx={styles.fileListItem}>
                  ・{file.name}
                </Typography>
              ))}
            </Box>
            <Typography sx={styles.creditNote}>
              ※ 文字読取を実行するとOCR枚数を消費します。
              <br />
              実行後は一覧画面で進捗を確認できます。
            </Typography>
            <Box sx={styles.buttonWrapper}>
              <Button sx={styles.skipButton} onClick={handleNavigateToOcrList}>
                <Typography sx={styles.skipButtonText}>スキップ</Typography>
              </Button>
              <Button sx={styles.ocrButton} onClick={handleOcrStart}>
                <Typography sx={styles.ocrButtonText}>文字読取を実行する</Typography>
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Typography sx={styles.singleDescription}>
              サイドバーから作成した読取パターンを選んで、
              <br />
              実際の業務を行いましょう。
            </Typography>
            <Button sx={styles.singleButton} onClick={handleNavigateToOcrList}>
              <Typography sx={styles.singleButtonText}>文字読取する</Typography>
            </Button>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default PatternCompleteModal;
